import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { ReactElement } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  CUSTOM_INSURANCE_ORG_CLAIM_FORMS,
  CUSTOM_INSURANCE_ORG_SUBMISSION_MECHANISMS,
  CUSTOM_INSURANCE_ORG_TYPES,
} from 'utils/lib/types/data/billing/custom-insurance-org.schemas';
import {
  CUSTOM_INSURANCE_ORG_CLAIM_FORM_LABELS,
  CUSTOM_INSURANCE_ORG_SUBMISSION_MECHANISM_LABELS,
  CUSTOM_INSURANCE_ORG_TYPE_LABELS,
} from 'utils/lib/types/data/billing/custom-insurance-org.types';
import { REQUIRED_FIELD_ERROR_MESSAGE } from 'utils/lib/validation/constants';
import { CustomInsuranceOrgForm } from '../../constants/customInsuranceOrg';
import { EmailInput } from '../input/EmailInput';
import { PhoneInput } from '../input/PhoneInput';
import { ContactsPanel } from '../nio/ContactsPanel';
import { NioAddressFields } from '../nio/NioAddressFields';

// The whole custom Insurance Organization form body — org fields on the left, contacts on the
// right — shared by the create dialog and the detail page's edit mode. Must render inside a
// FormProvider whose values are an CustomInsuranceOrgForm.
export function CustomInsuranceOrgFormFields(): ReactElement {
  const { control } = useFormContext<CustomInsuranceOrgForm>();
  const submissionMechanism = useWatch({ control, name: 'submissionMechanism' });
  return (
    <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      <Box sx={{ flex: 2, minWidth: 420, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          name="name"
          control={control}
          rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              label="Organization Name *"
              size="small"
              fullWidth
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name="orgId"
          control={control}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
            pattern: { value: /^OTR-.+$/, message: 'Id must start with "OTR-"' },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              label="Id *"
              placeholder="OTR-"
              size="small"
              fullWidth
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!error}
              helperText={error?.message ?? 'Must start with "OTR-"'}
            />
          )}
        />

        <Typography variant="subtitle2" color="text.secondary">
          Insurance Type
        </Typography>
        <Controller
          name="insuranceTypes"
          control={control}
          render={({ field }) => (
            <FormGroup row>
              {CUSTOM_INSURANCE_ORG_TYPES.map((type) => (
                <FormControlLabel
                  key={type}
                  label={CUSTOM_INSURANCE_ORG_TYPE_LABELS[type]}
                  control={
                    <Checkbox
                      size="small"
                      checked={field.value.includes(type)}
                      onChange={(e) => {
                        field.onChange(
                          e.target.checked ? [...field.value, type] : field.value.filter((value) => value !== type)
                        );
                      }}
                    />
                  }
                />
              ))}
            </FormGroup>
          )}
        />

        <Controller
          name="submissionMechanism"
          control={control}
          rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error}>
              <FormLabel sx={{ fontSize: 14 }}>Submission Mechanism *</FormLabel>
              <RadioGroup row value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                {CUSTOM_INSURANCE_ORG_SUBMISSION_MECHANISMS.map((mechanism) => (
                  <FormControlLabel
                    key={mechanism}
                    value={mechanism}
                    control={<Radio size="small" />}
                    label={CUSTOM_INSURANCE_ORG_SUBMISSION_MECHANISM_LABELS[mechanism]}
                  />
                ))}
              </RadioGroup>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />

        {submissionMechanism === 'email' && <EmailInput name="submissionDetails.email" label="Email Address" />}

        {submissionMechanism === 'portal' && (
          <>
            <Controller
              name="submissionDetails.portalUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Portal URL"
                  size="small"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
            <Controller
              name="submissionDetails.portalDetails"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Portal Details"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </>
        )}

        {submissionMechanism === 'fax' && (
          <PhoneInput name="submissionDetails.faxNumber" label="Fax Number" fieldLabel="Fax number" />
        )}

        {submissionMechanism === 'mail' && <NioAddressFields prefix="submissionDetails.mailAddress" />}

        <Controller
          name="acceptedClaimForm"
          control={control}
          rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error}>
              <FormLabel sx={{ fontSize: 14 }}>Accepted Claim Form *</FormLabel>
              <RadioGroup row value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                {CUSTOM_INSURANCE_ORG_CLAIM_FORMS.map((claimForm) => (
                  <FormControlLabel
                    key={claimForm}
                    value={claimForm}
                    control={<Radio size="small" />}
                    label={CUSTOM_INSURANCE_ORG_CLAIM_FORM_LABELS[claimForm]}
                  />
                ))}
              </RadioGroup>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 280,
          borderLeft: { md: 1 },
          borderColor: { md: 'divider' },
          pl: { md: 5 },
        }}
      >
        <ContactsPanel />
      </Box>
    </Box>
  );
}
